<!--
  One document block.

  A direct port of renderBlock() from the old app.js: same element choices,
  same data-id on the outermost element of every block, same fallback for a
  type this build does not recognise.

  What is gone is the hand-rolled reconcile(): the keyed {#each} in DocView
  gives Svelte the block ids, so it moves and patches existing DOM rather than
  rebuilding it, which is exactly what reconcile() was written to do.

  Every block the server's comment-anchor scan searches -- a paragraph or
  heading, a paragraph inside a list item or a table cell, a code block --
  carries `data-text-block` with ITS id, so a selection can be mapped to the
  block and characters an anchor names (lib/selection.ts), and draws its
  comment highlights. A paragraph nested in an item or a cell is otherwise
  rendered bare, so it gets a span to carry the attribute.

  A mermaid code block is a rendered diagram with no text to carry it: its
  host carries `data-diagram` instead, and MermaidBlock draws its ranges on
  the labels (TASK-045).
-->
<script lang="ts">
  import type { PMNode, Question } from '../types'
  import Self from './Block.svelte'
  import Inlines from './Inlines.svelte'
  import PendingBlock from './PendingBlock.svelte'
  import MermaidBlock from './MermaidBlock.svelte'
  import { splitText } from '../anchors'
  import { useHighlights } from '../highlights.svelte'

  let {
    node,
    questions,
    onrendered,
  }: { node: PMNode; questions: Question[]; onrendered?: () => void } = $props()

  const id = $derived(node.attrs?.id as string | undefined)

  const heading = $derived(node.attrs?.heading as number | undefined)
  const headingTag = $derived(heading === 1 ? 'h1' : heading === 2 ? 'h2' : 'h3')

  const listKind = $derived(node.attrs?.kind as string | undefined)
  const codeText = $derived((node.content ?? []).map((c) => c.text ?? '').join(''))

  const hl = useHighlights()
  const codeRanges = $derived(node.type === 'codeBlock' && hl ? hl.rangesFor(id) : [])
  const codePieces = $derived(splitText(codeText, codeRanges))
</script>

{#if node.type === 'paragraph'}
  {#if heading}
    <svelte:element
      this={headingTag}
      data-id={id}
      data-text-block={id}
      class={heading === 1 ? 'doc-title' : undefined}
    ><Inlines nodes={node.content ?? []} blockId={id} /></svelte:element>
  {:else}
    <p data-id={id} data-text-block={id}><Inlines nodes={node.content ?? []} blockId={id} /></p>
  {/if}

{:else if node.type === 'pending'}
  <PendingBlock {node} {questions} />

{:else if node.type === 'list'}
  <svelte:element
    this={listKind === 'ordered' ? 'ol' : 'ul'}
    data-id={id}
    class={listKind === 'check' ? 'checklist' : undefined}
  >
    {#each node.content ?? [] as item, i (item.attrs?.id ?? i)}
      <li data-id={item.attrs?.id}>
        {#if listKind === 'check'}
          <!-- The box and the text are siblings in a flex row, so the text
               wraps under itself rather than under the box. A checked item is
               read-only here: the viewer is read-only until the editor lands. -->
          <input type="checkbox" disabled checked={Boolean(item.attrs?.checked)} />
          <span
            >{#each item.content ?? [] as child, j (child.attrs?.id ?? j)}{#if child.type === 'paragraph'}<span data-text-block={child.attrs?.id}><Inlines
                    nodes={child.content ?? []}
                    blockId={child.attrs?.id}
                  /></span>{:else}<Self node={child} {questions} {onrendered} />{/if}{/each}</span
          >
        {:else}
          <!-- No wrapper: a nested list inside a <span> is invalid nesting and
               browsers reflow it out of the span anyway. -->
          {#each item.content ?? [] as child, j (child.attrs?.id ?? j)}{#if child.type === 'paragraph'}<span data-text-block={child.attrs?.id}><Inlines
                  nodes={child.content ?? []}
                  blockId={child.attrs?.id}
                /></span>{:else}<Self node={child} {questions} {onrendered} />{/if}{/each}
        {/if}
      </li>
    {/each}
  </svelte:element>

{:else if node.type === 'table'}
  <!--
    A wide table scrolls inside a wrapper rather than through
    `table { display: block }`, which drops the implicit table role in some
    screen readers and with it the row and column relationships. tabindex keeps
    the scroll region reachable from the keyboard, and the wrapper carries the
    block's data-id so the scroll anchor still finds the outermost element.
    TASK-034.
  -->
  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <div class="table-wrap" data-id={id} role="region" tabindex="0" aria-label="Table">
    <table>
      <tbody>
        {#each node.content ?? [] as row, i (row.attrs?.id ?? i)}
          <tr data-id={row.attrs?.id}>
            {#each row.content ?? [] as cell, j (cell.attrs?.id ?? j)}
              <svelte:element
                this={i === 0 || cell.attrs?.header ? 'th' : 'td'}
                data-id={cell.attrs?.id}
              >
                {#each cell.content ?? [] as child, k (child.attrs?.id ?? k)}{#if child.type === 'paragraph'}<span data-text-block={child.attrs?.id}><Inlines
                        nodes={child.content ?? []}
                        blockId={child.attrs?.id}
                      /></span>{:else}<Self node={child} {questions} {onrendered} />{/if}{/each}
              </svelte:element>
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>

{:else if node.type === 'codeBlock'}
  <!-- A diagram draws its ranges itself, on what mermaid drew (TASK-045); if
       mermaid cannot draw it, it falls back to this same code block, so a
       broken diagram can still be commented on. -->
  {#snippet code()}
    <!-- One line: whitespace inside <pre> is content. -->
    <pre data-id={id}><code data-text-block={id}>{#each codePieces as piece, i (i)}{#if piece.cover}<mark class="anchor" data-threads={piece.cover.threads.join(' ')} data-state={piece.cover.state} data-active={hl?.active && piece.cover.threads.includes(hl.active) ? '' : undefined}>{piece.text}</mark>{:else}{piece.text}{/if}{/each}</code>{#each codeRanges as r (r.threadId)}<button type="button" class="anchor-pin" data-anchor-pin data-thread-id={r.threadId} data-state={r.state} aria-label={`Go to the ${r.state} comment on “${r.text.length > 60 ? `${r.text.slice(0, 59)}…` : r.text}”`} onclick={() => hl?.activate(r.threadId)} onfocus={() => hl && (hl.active = r.threadId)} onblur={() => hl && hl.active === r.threadId && (hl.active = null)}></button>{/each}</pre>
  {/snippet}
  {#if node.attrs?.language === 'mermaid'}
    <MermaidBlock src={codeText} {id} ranges={codeRanges} fallback={code} {onrendered} />
  {:else}
    {@render code()}
  {/if}

{:else if node.type === 'blockquote'}
  <blockquote data-id={id}>
    {#each node.content ?? [] as child, i (child.attrs?.id ?? i)}
      <Self node={child} {questions} {onrendered} />
    {/each}
  </blockquote>

{:else if node.type === 'horizontalRule'}
  <hr data-id={id} />

{:else}
  <div data-id={id}>
    {#each node.content ?? [] as child, i (child.attrs?.id ?? i)}
      <Self node={child} {questions} {onrendered} />
    {/each}
  </div>
{/if}
